/** @jsxImportSource minimal-view */

import { Rect, clamp } from 'geometrik'
import { element, on, queue, view, web } from 'minimal-view'
import { Param } from '../vm/interface'
import { Knob } from './knob'
import { screen } from './screen'
import { skin } from './skin'
import { observe } from './util/observe'
import { hmr } from './hmr'
import { SINGLE_CLICK_MS } from './constants'

export type Slider = typeof Slider.State

export const Slider = hmr(web(view('slider',
  class props {
    name!: string
    range!: Knob
    param!: Param
    onClick!: () => void
    onRangeUpdate!: () => void
    onPointerMove!: (e: PointerEvent) => void
    onPointerDown!: (e: PointerEvent) => void
    onPointerUp!: (e: PointerEvent) => void
    onDraw!: () => void
  },
  class local {
    host = element
    canvas?: HTMLCanvasElement
    c?: CanvasRenderingContext2D
    rect?: Rect
  },
  function actions({ $, fn, fns }) {
    return fns(new class actions {
      handleDown = fn(({ range, rect }) => (e: PointerEvent) => {
        // $.isHandlingRange = true
        const x = e.pageX
        const y = e.pageY
        const startValue = range.value
        range.active = true

        let downTime = performance.now()

        const onPointerMove = queue.raf((e: PointerEvent) => {
          const width = rect.width
          const dx = e.pageX - x
          const dy = y - e.pageY
          const normal = dx / width + dy / width
          range.value = clamp(0, 1, startValue + normal)

          $.onRangeUpdate()
          this.draw()
          // requestAnimationFrame(this.draw)
        })

        const off = on(window, 'pointermove').capture((e) => {
          e.stopPropagation()
          onPointerMove(e)
        })

        on(window, 'pointerup').once((e) => {
          if (performance.now() - downTime < SINGLE_CLICK_MS) {
            $.onClick()
          }

          off()
          // $.isHandlingRange = false
          range.active = false
          // $.ranges = [...$.ranges!]
          // this.draw()
          requestAnimationFrame(this.draw)

          $.onPointerUp?.(e)
        })

        this.draw()

        $.onPointerDown?.(e)
      })

      handleMove = fn(({ range }) => (e: PointerEvent) => {
        if (!range.hover) {
          range.hover = true
          this.draw()
        }
      })

      handleLeave = fn(({ range }) => (e: PointerEvent) => {
        if (range.hover) {
          range.hover = false
          this.draw()
        }
      })

      handleWheel = fn(({ range }) => (e: WheelEvent) => {
        e.preventDefault()
        e.stopPropagation()

        range.value = clamp(0, 1, range.value + e.deltaY * 0.0003)
        $.onRangeUpdate()
        this.draw()
      })

      draw = fn(({ host, range, param, rect: r, c }) => () => {
        c.save()

        c.clearRect(0, 0, r.width, r.height)
        c.fillStyle = skin.$.colors.bg
        c.fillRect(0, 0, r.width, r.height)

        const rangeRect = r.clone().sub(r.pos) //.zoomLinear(-1)
        range.draw(
          c,
          rangeRect.x,
          rangeRect.y,
          rangeRect.width,
          rangeRect.height
        )

        // c.globalCompositeOperation = 'hard-light'
        c.strokeStyle = c.fillStyle = '#aaf'
        c.strokeStyle = '#000'

        const fontSize = 11.5
        c.font = `400 ${fontSize}px ${skin.$.fonts.mono}`

        c.textBaseline = 'middle'
        c.lineWidth = fontSize / 5

        const text = param.name!.text.slice(1, -1)
        const y = rangeRect.height / 2 + rangeRect.y + 1
        // c.strokeText(text, 10, y)
        c.fillStyle = '#000'
        c.fillText(text, 11, y + 1)
        c.fillStyle = '#fff'
        c.fillText(text, 10, y)

        c.restore()

        $.onDraw()
      })

      resize = fn(({ host }) => async () => {
        const rect = host.getBoundingClientRect()
        if (!rect.width || !rect.height) return
        const r = new Rect(rect).roundSelf()
        if (!$.rect || r.width !== $.rect.width || r.height !== $.rect.height) {
          $.rect = r
        }
      })
    })
  },
  function effects({ $, fx, deps, refs }) {
    fx(() => skin.fx(({ fonts, colors }) => {
      $.css = /*css*/`
      & {
        display: flex;
        width: 100%;
        height: 100%;
      }

      canvas {
        height: 100%;
        width: 100%;
      }
      `
    }))

    fx(({ host }) =>
      observe.resize.initial(host, $.resize)
    )

    fx(({ canvas }) => {
      $.c = canvas.getContext('2d')!
    })

    fx(() => screen.fx(({ pr }) =>
      fx(({ canvas, rect, c }) => {
        canvas.width = rect.width * pr
        canvas.height = rect.height * pr
        // canvas.style.width = rect.width + 'px'
        // canvas.style.height = rect.height + 'px'
        // canvas.style.imageRendering = 'pixelated'

        c.scale(pr, pr)
      })
    ))

    fx.raf(({
      name,
      rect,
    }) => {
      $.draw()
    })

    fx(({ host, range }) => {
      range.slider = host as unknown as Slider
    })

    fx(() => {
      $.view = <canvas
        ref={refs.canvas}
        onpointermove={$.handleMove}
        onpointerdown={$.handleDown}
        onpointerleave={$.handleLeave}
        onwheel={$.handleWheel}
      />
    })
  }
)))
